import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useDatos } from '../contexts/DatosContext';
import type { Clase } from '../types';
import { bloqueVacio, bloquesDesdePlantilla, claseNueva, duracionPlantilla } from '../utils/clase';
import { Boton } from '../components/ui/Boton';
import { Aviso } from '../components/ui/Aviso';
import { Modal } from '../components/ui/Modal';
import { Paso1Grupo, type CreationMode } from '../components/asistente/Paso1Grupo';
import { Paso2Logistica } from '../components/asistente/Paso2Logistica';
import { Paso3Bloques } from '../components/asistente/Paso3Bloques';
import { Paso4Revision } from '../components/asistente/Paso4Revision';
const PASOS = ['Conocé el punto de partida', 'Prepará el espacio', 'Construí la clase', 'Revisá antes de guardar'];
export function NuevaClase() {
  const {
    id
  } = useParams();
  const navegar = useNavigate();
  const {
    clases,
    borrador,
    guardarBorrador,
    limpiarBorrador,
    guardarClase,
    recursos,
    favoritos,
    recientes,
    alternarFavorito,
    registrarReciente,
    notificar,
    preferencias,
    guardarPreferencias
  } = useDatos();
  const claseGuardada = useMemo(() => id ? clases.find(c => c.id === id) : undefined, [id, clases]);
  const [clase, setClase] = useState<Clase>(() => {
    if (id) return claseGuardada || claseNueva();
    if (borrador) return borrador;
    return {
      ...claseNueva(),
      nivel: preferencias.ultimoNivel
    };
  });
  const [paso, setPaso] = useState(1);
  const [creationMode, setCreationMode] = useState<CreationMode>(null);
  const [estructuraAplicada, setEstructuraAplicada] = useState<string | null>(null);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [avisoLogistica, setAvisoLogistica] = useState(false);
  const [borradorRecuperado, setBorradorRecuperado] = useState(() => !id && Boolean(borrador));
  const ultimoSerializado = useRef<string>('');
  useEffect(() => {
    if (id && claseGuardada && claseGuardada.id !== clase.id) {
      setClase(claseGuardada);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, claseGuardada?.id]);
  useEffect(() => {
    const serializado = JSON.stringify(clase);
    if (serializado === ultimoSerializado.current) return;
    ultimoSerializado.current = serializado;
    const temporizador = window.setTimeout(() => {
      if (clases.some(c => c.id === clase.id)) {
        guardarClase(clase);
      } else {
        guardarBorrador(clase);
      }
    }, 600);
    return () => window.clearTimeout(temporizador);
  }, [clase, clases, guardarClase, guardarBorrador]);
  const actualizar = (parcial: Partial<Clase>) => {
    setClase(prev => {
      const siguiente = {
        ...prev,
        ...parcial
      };
      if (parcial.nivel && parcial.nivel !== prev.nivel) {
        guardarPreferencias({
          ultimoNivel: parcial.nivel
        });
      }
      return siguiente;
    });
  };
  const validarPaso1 = () => {
    const nuevos: Record<string, string> = {};
    if (!clase.titulo.trim()) nuevos.titulo = 'El título es obligatorio.';
    if (!Number.isInteger(clase.participantes) || clase.participantes < 1 || clase.participantes > 40) nuevos.participantes = 'Ingresá un número entero entre 1 y 40.';
    if (!Number.isInteger(clase.puntos) || clase.puntos < 1 || clase.puntos > 20) nuevos.puntos = 'Ingresá un número entero entre 1 y 20.';
    if (clase.duracion < 30 || clase.duracion > 180) nuevos.duracion = 'La duración debe estar entre 30 y 180 minutos.';
    setErrores(nuevos);
    return Object.keys(nuevos).length === 0;
  };
  const aplicarModoCreacion = () => {
    if (!creationMode) return false;
    const claveEstructura = `${creationMode}:${clase.nivel}`;
    if (claveEstructura !== estructuraAplicada) {
      if (creationMode === 'blank') {
        actualizar({
          bloques: [bloqueVacio('Bloque 1')]
        });
      } else {
        actualizar({
          bloques: bloquesDesdePlantilla(clase.nivel),
          duracion: duracionPlantilla(clase.nivel)
        });
      }
      setEstructuraAplicada(claveEstructura);
    }
    return true;
  };
  const continuar = () => {
    if (paso === 1) {
      if (!creationMode || !validarPaso1()) return;
      aplicarModoCreacion();
    }
    if (paso === 2) {
      const faltan = Object.values(clase.logistica.checks).some(v => !v);
      if (faltan && !avisoLogistica) {
        setAvisoLogistica(true);
        return;
      }
    }
    setPaso(p => Math.min(4, p + 1));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const guardar = (imprimir: boolean) => {
    if (!validarPaso1()) {
      setPaso(1);
      return;
    }
    const aGuardar: Clase = {
      ...clase,
      estado: clase.estado === 'Borrador' ? 'Planificada' : clase.estado
    };
    const guardada = guardarClase(aGuardar);
    if (!id && borrador && borrador.id === clase.id) limpiarBorrador();
    notificar('Clase guardada en este dispositivo.');
    if (imprimir) navegar(`/imprimir/${guardada.id}`);else navegar('/clases');
  };
  return <div className="space-y-5" data-id="element-762" data-mp="xlj7n5pbxgew">
      <div data-id="element-763" data-mp="xmc7dmpvf5nj">
        <h1 className="text-2xl font-extrabold text-tinta-titulo sm:text-3xl" data-id="element-764" data-mp="xkhsoq5709rw">
          {id ? 'Editar clase' : 'Nueva clase'}
        </h1>
        <p className="mt-1 text-sm text-tinta-sec" data-id="element-765" data-mp="17bvsz04yxdm3">Paso {paso} de 4 · {PASOS[paso - 1]}</p>
      </div>

      <ol className="flex flex-wrap gap-2" aria-label="Pasos del asistente" data-id="element-766" data-mp="jm1eyf8wxj09">
        {PASOS.map((p, i) => {
        const numero = i + 1;
        const activo = numero === paso;
        const completado = numero < paso;
        return <li key={p} data-id="element-767" data-mp="sleot90y89li" data-mpi={i}>
              <button type="button" onClick={() => {
            if (numero > 1 && paso === 1) {
              if (!creationMode || !validarPaso1()) {
                setPaso(1);
                return;
              }
              aplicarModoCreacion();
            } else if (numero > paso && !validarPaso1()) {
              setPaso(1);
              return;
            }
            setPaso(numero);
          }} aria-current={activo ? 'step' : undefined} aria-disabled={paso === 1 && numero > 1 && creationMode === null} className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-3 text-sm font-semibold ${activo ? 'border-vino bg-vino text-white' : completado ? 'border-verde/40 bg-verde/5 text-tinta' : 'border-linea bg-white text-tinta-sec'}`} data-id="element-768" data-mp="noelv6zebmym">
                {completado ? <Check className="h-4 w-4 text-verde" aria-hidden="true" data-id="element-769" data-mp="rsx34dsbinh7" /> : <span aria-hidden="true" data-id="element-770" data-mp="3qf8bjplutbyi">{numero}</span>}
                <span className="hidden sm:inline" data-id="element-771" data-mp="3veky098tch0k">{p}</span>
                <span className="sm:hidden" data-id="element-772" data-mp="3nx66tmk71wbw">Paso {numero}</span>
              </button>
            </li>;
      })}
      </ol>

      {borradorRecuperado && <Aviso tipo="info" titulo="Borrador recuperado" data-id="element-773" data-mp="3ivqqu8wvig0d">
          <p data-id="element-774" data-mp="3dx5lxw6of85l">Retomaste el borrador guardado en este dispositivo.</p>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-775" data-mp="39xeqhf67g3uq">
            <Boton tamano="sm" variante="contorno" onClick={() => setBorradorRecuperado(false)} data-id="element-776" data-mp="39yy5z3k1eavg">
              Continuar con el borrador
            </Boton>
            <Boton tamano="sm" variante="texto" onClick={() => {
          limpiarBorrador();
          setClase({
            ...claseNueva(),
            nivel: preferencias.ultimoNivel
          });
          setCreationMode(null);
          setEstructuraAplicada(null);
          setBorradorRecuperado(false);
          setPaso(1);
        }} data-id="element-777" data-mp="3058yw6jv7og2">
              Empezar una clase nueva
            </Boton>
          </div>
        </Aviso>}

      {paso === 1 && <Paso1Grupo clase={clase} actualizar={actualizar} errores={errores} creationMode={creationMode} onCreationModeChange={setCreationMode} data-id="element-778" data-mp="dz4gk1syalkn" />}
      {paso === 2 && <Paso2Logistica clase={clase} actualizar={actualizar} data-id="element-779" data-mp="3zdn83g4mcm5" />}
      {paso === 3 && <Paso3Bloques clase={clase} actualizar={actualizar} recursos={recursos} favoritos={favoritos} recientes={recientes} alternarFavorito={alternarFavorito} registrarReciente={registrarReciente} data-id="element-780" data-mp="3me4kv05k0fc6" />}
      {paso === 4 && <Paso4Revision clase={clase} actualizar={actualizar} recursos={recursos} data-id="element-781" data-mp="3hejpt0ti8r4q" />}

      <div className="sticky bottom-16 z-20 rounded-2xl border border-linea bg-white p-3 lg:bottom-4" data-id="element-782" data-mp="33f0fq2h4v5ps">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-783" data-mp="38f4w7si1k0g0">
          <div className="flex flex-wrap gap-2" data-id="element-784" data-mp="2ostnrtezut6m">
            {paso > 1 && <Boton variante="contorno" onClick={() => setPaso(p => p - 1)} data-id="element-785" data-mp="2ouxpc4pf7g3q">
                Volver a editar
              </Boton>}
            <Boton variante="texto" onClick={() => {
            if (clases.some(c => c.id === clase.id)) guardarClase(clase);else guardarBorrador(clase);
            notificar('Borrador guardado en este dispositivo.');
          }} data-id="element-786" data-mp="2xtru4icwncsd">
              Guardar borrador
            </Boton>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-787" data-mp="3hhirm07fpcm2">
            {paso < 4 ? <Boton disabled={paso === 1 && creationMode === null} onClick={continuar} data-id="element-788" data-mp="3hjlfoep10vo5">
                Continuar
              </Boton> : <>
                <Boton variante="secundario" onClick={() => guardar(true)} data-id="element-789" data-mp="3qiyt1ig9u4e5">
                  Guardar y preparar impresión
                </Boton>
                <Boton onClick={() => guardar(false)} data-id="element-790" data-mp="3osd0becht1jk">Guardar clase</Boton>
              </>}
          </div>
        </div>
      </div>

      <Modal abierto={avisoLogistica} titulo="Controles sin confirmar" onCerrar={() => setAvisoLogistica(false)} ancho="sm" acciones={<>
            <Boton variante="contorno" onClick={() => setAvisoLogistica(false)} data-id="element-792" data-mp="3a4i8s1a81382">
              Volver a revisar
            </Boton>
            <Boton onClick={() => {
        setAvisoLogistica(false);
        setPaso(3);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }} data-id="element-793" data-mp="bl1uj8pi3dxd">
              Continuar bajo mi criterio
            </Boton>
          </>} data-id="element-791" data-mp="3k0lhxr3hq24i">
        <p className="text-sm text-tinta" data-id="element-794" data-mp="3storhob0lutv">
          Quedaron controles sin confirmar. Podés continuar bajo tu criterio profesional, pero
          revisalos antes de comenzar la clase.
        </p>
      </Modal>
    </div>;
}